"use client"

import ArticleBox from '../../components/ArticleBox/ArticleBox';
import { useEffect, useState } from "react";
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { storyblokInit, apiPlugin, getStoryblokApi } from "@storyblok/react";

// ✅ Dynamically import Lottie with SSR disabled
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import blogLottie from "../../assets/bloglottie.json";

storyblokInit({
  accessToken:process.env.NEXT_PUBLIC_STORYBLOK_TOKEN,
  use: [apiPlugin], // Enable API Plugin
});

function ArticlesPage({ blok }) {
    const [allBlogs, setAllBlogs] = useState([]); // Stores all fetched blogs
    const [filteredBlogs, setFilteredBlogs] = useState([]); // Stores blogs for display
    const [tags, setTags] = useState([]);
    const [filter, setFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const perPage = 9;

    useEffect(() => {
        const fetchAllBlogs = async () => {
            setLoading(true);
            const storyblokApi = getStoryblokApi();
            let blogs = [];
            let page = 1;
            let hasMore = true;

            try {
                while (hasMore) {
                    const { data } = await storyblokApi.get("cdn/stories", {
                        starts_with: "articles/",
                        per_page: 100, // Max per API call
                        page: page,
                    });

                    blogs = [...blogs, ...data.stories];

                    // If returned stories are less than 100, it means no more data
                    hasMore = data.stories.length === 100;
                    page++;
                }

                const { data: tagList } = await storyblokApi.get("cdn/tags", {
                    cv: new Date().getTime()
                });

                setTags(tagList.tags);


                // Convert date strings to proper Date objects for sorting
                blogs = blogs?.map(blog => {
                    let blogItem = blog?.content?.Blog.find(item => !['global_header', 'global_reference'].includes(item?.component));
                    if (blogItem?.date) {
                        blogItem.parsedDate = new Date(Date.parse(blogItem.date)); // Convert date
                    }
                    return { ...blog, blogItem };
                });

                // Sort blogs by date (newest first)
                blogs.sort((a, b) => (b.blogItem?.parsedDate || 0) - (a.blogItem?.parsedDate || 0));

                setAllBlogs(blogs);
                setFilteredBlogs(blogs.slice(0, perPage));
            } catch (err) {
                console.error("Error fetching blogs:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAllBlogs();
    }, []);

    useEffect(() => {
        let filtered = allBlogs;

        // Apply filter if selected
        if (filter) {
            filtered = allBlogs.filter(blog =>
                blog.tag_list.includes(filter)
            );
        }

        // Reset pagination and apply it to filteredBlogs
        setCurrentPage(1);
        setFilteredBlogs(filtered.slice(0, perPage));
    }, [filter, allBlogs]);

    const handleLoadMore = () => {
        const nextPage = currentPage + 1;
        const nextBlogs = allBlogs.slice(0, nextPage * perPage);
        setFilteredBlogs(nextBlogs);
        setCurrentPage(nextPage);
    };

    return (
        <div className='m-auto w-full'>
            <div className='bg-asmPageTopBG' style={{
                backgroundSize: "cover",
                backgroundPosition: "bottom",
                backgroundRepeat: "repeat",
                margin: 'auto'
            }}>
                <div className='m-auto'>
                    <div className="relative lg:min-h-[1000px] lg:flex flex-col items-center py-8 m-auto">
                        <div className="font-aileron pt-20 lg:py-32 text-center font-bold max-w-[1920px]">
                            <div className="bg-custom-radial bg-clip-text text-transparent text-[48px] lg:text-[80px] p-4 lg:p-0">
                                {blok.title}
                            </div>
                        </div>
                        <div className='w-full lg:h-[160px] bg-VMTakeActionBg lg:-mt-20 flex justify-center'>
                            <div className='lg:flex justify-center items-center lg:gap-20 p-10 lg:p-0 max-w-[1920px]'>
                                <p className='!text-white font-lato font-semi-bold lg:text-[32px] text-[20px] text-center lg:text-start'>{blok.subtitle}</p>
                                <div className='lg:justify-items-start lg:w-[400px] mt-10 lg:mt-0'>
                                    <label className="text-[18px] !text-white lg:text-[24px] font-bold">Select category</label>
                                    <select
                                        className="mt-1 p-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm h-14 bg-white"
                                        onChange={(e) => setFilter(e.target.value)}
                                    >
                                        <option value="">- Select -</option>
                                        {tags.map((item, index) => <option key={index} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className='lg:w-[1500px] lg:p-0 p-4 space-y-[40px]'>
                            <div className='flex flex-col items-center text-center justify-center w-[100%] m-auto py-14'>
                                {loading && currentPage === 1 ? (
                                    <Lottie animationData={blogLottie} className='w-48 h-48' />
                                ) : filteredBlogs.length === 0 ? (
                                    <p className='text-gray-500 text-lg'>No Blogs Found</p>
                                ) : (
                                    <div className='grid lg:grid-cols-3 gap-10'>
                                        {filteredBlogs.map((item, i) => (
                                            <ArticleBox
                                                date={item.blogItem?.date}
                                                description={item.blogItem?.content1?.content?.[0]?.content?.[0]?.text?.substring(0, 100)}
                                                image={item.blogItem?.blogImg?.filename}
                                                title={item?.name}
                                                link={`/${item?.full_slug}`}
                                                key={i}
                                            />
                                        ))}
                                    </div>
                                )}
                                {filteredBlogs.length < allBlogs.length && (
                                    <button onClick={handleLoadMore} className="mt-10 bg-[#E56717] !text-white py-4 px-4 rounded-md hover:bg-orange-600 transition w-full lg:w-[260px] sm:w-auto font-lato text-[14px] flex items-center justify-center">
                                        {loading ? <Lottie animationData={blogLottie} className='w-6 h-6' /> : "Load More"}
                                    </button>
                                )}
                                {filteredBlogs.length >= allBlogs.length && <p className='text-gray-500 text-lg mt-10'>No More Blogs</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

ArticlesPage.propTypes = {
    blok: PropTypes.object.isRequired
};

export default ArticlesPage;
